import React, { useCallback, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Tooltip } from 'react-tooltip';
import TrackingAPI from 'api/TrackingAPI';
import brackets_icon from 'assets/brackets.svg';
import camera_icon from 'assets/camera_white_icon.svg';
import chat_icon from 'assets/message-square.svg';
import send_img from 'assets/send.svg';
import send_inactive_img from 'assets/send_inactive.svg';
import TokenIcon from "assets/token.svg?react";
import { SuggestionsButton } from 'components/ai/tools/SuggestionButton';
import SubscriptionModal from 'components/modals/SubscriptionModal/SubscriptionModal';
import BuyTokensModal from 'modals/BuyTokensModal';
import { useMappedState } from 'redux-react-hook';
import { useGetTokenCostsQuery } from 'services/api/tokenApi';
import styled from 'styled-components';

const UserInputComponent = (props) => {
  const {
    dialog,
    scenario,
    setNewMessageText,
    newMessageText,
    inputRef,
    hasCharactersWithLore,
    isPrivate,
    badModelCountdownVisible,
    onMessageSubmit,
    chatMode,
    setChatMode,
    sending,
    suggestionUsage,
    setModals,
    batches,
    game,
    initialSuggestion,
  } = props;

  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isBuyTokensModalOpen, setIsBuyTokensModalOpen] = useState(false);
  const [isOpenSuggestionMenu, setIsOpenSuggestionMenu] = useState(false);
  const { data: tokenCosts } = useGetTokenCostsQuery(undefined, {
    selectFromResult: ({ data }) => ({ data: data ?? null })
  });

  const { currentUser, hasSubscription } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        hasSubscription: hasSubscription
      };
    }, [])
  );

  const customDialogPhotoGenerationCost = tokenCosts?.dialogPhotoGenerationCost;
  const tokenBalance = currentUser?.tokenBalance ?? 0.0;
  const hasUserMessage = batches.some(batch => batch?.userMessage);
  const isInitialSuggestionTooltipOpen = !hasUserMessage && initialSuggestion?.viewText && !isOpenSuggestionMenu && !sending;

  const handleMessageSubmit = async () => {
    if (newMessageText.trim() === '') {
      if (isCameraIcon && hasSubscription && (isPrivate || (!isPrivate && hasCharactersWithLore))) {
        if (tokenBalance < customDialogPhotoGenerationCost) {
          setIsBuyTokensModalOpen(true);
          return;
        }

        await onMessageSubmit('', false, () => false, true);
      }
      return;
    }
    await onMessageSubmit();
  };

  const getSendButtonIcon = () => {
    if (sending) {
      return {
        src: send_inactive_img,
        alt: 'send',
        className: 'btn btn-primary'
      };
    }

    if (newMessageText.trim() === '') {
      if (hasSubscription && (isPrivate || (!isPrivate && hasCharactersWithLore))) {
        return {
          src: camera_icon,
          alt: 'camera',
          className: 'btn btn-primary'
        };
      }
      return {
        src: send_inactive_img,
        alt: 'send',
        className: 'btn btn-primary'
      };
    }

    return {
      src: send_img,
      alt: 'send',
      className: 'btn btn-primary'
    };
  };

  const iconProps = getSendButtonIcon();
  const isCameraIcon = iconProps.src === camera_icon;

  const handleInitialSuggestionClick = async () => {
    const analyticsData = {
      config_name: scenario?.name,
      scenario_name: game?.name,
      user_id: currentUser?.id
    };


    TrackingAPI.trackEvent('first_message_popup_clicked', analyticsData);
    if (!sending) {
      await onMessageSubmit(initialSuggestion.sendedText);
    }
  };

  const renderSendButton = () => {
    const isInactiveButton = iconProps.src === send_inactive_img;
    const showCameraIcon = isCameraIcon && hasSubscription && (isPrivate || (!isPrivate && hasCharactersWithLore));

    return (
      <SendButton
        sending={sending}
        className={'ml-4'}
        onClick={async () => {
          await handleMessageSubmit();
        }}
        $isCameraIcon={showCameraIcon}
        $isInactiveButton={isInactiveButton}
      >
        {showCameraIcon ? (
          <>
            <CameraSection>
              <ArrowImg {...iconProps} />
            </CameraSection>
            <TokenSection>
              <TokenAmount>{customDialogPhotoGenerationCost}</TokenAmount>
              <TokenIcon width={14} height={14} />
            </TokenSection>
          </>
        ) : (
          <ArrowImg {...iconProps} />
        )}
      </SendButton>
    );
  };

  return (
    <UserInputPlaceholder>
      <div className={'flex flex-row'}>
        <SuggestionsButton
          dialogId={dialog?.id}
          setNewMessageText={newMessage => setNewMessageText(newMessage)}
          onClickCheck={() => setModals('suggestions_button_click')}
          suggestionUsage={suggestionUsage}
          isOpenSuggestionMenu={isOpenSuggestionMenu}
          setIsOpenSuggestionMenu={setIsOpenSuggestionMenu}
          isInitialSuggestionTooltipOpen={isInitialSuggestionTooltipOpen}
          game={game}
          scenario={scenario}
        />
        {isInitialSuggestionTooltipOpen && (
          <StyledTooltip
            id="initial-suggestion"
            place="top-start"
            className="suggestion-tooltip"
            isOpen={true}
            anchorSelect=".suggestion-button"
            clickable={true}
            content={
              <TooltipContent>
                <div
                  onClick={handleInitialSuggestionClick}
                >
                  <InitialSuggestionTooltip>
                    {initialSuggestion.viewText}
                  </InitialSuggestionTooltip>
                </div>
              </TooltipContent>
            }
          />
        )}
        <div className={'relative flex-1'}>
          <UserInput
            type={'text'}
            ref={inputRef}
            autocomplete={'off'}
            className={'form-control w-full rounded focus:ring-0'}
            style={{ paddingLeft: 75 }}
            placeholder={isMobile ? 'Type here' : 'Type your message here'}
            value={newMessageText}
            onChange={e => {
              setNewMessageText(e.target.value);
            }}
            disabled={badModelCountdownVisible}
            onKeyDown={async e => {
              if (e.key !== 'Enter') {
                return;
              }
              e.preventDefault();
              e.stopPropagation();
              
              if (newMessageText.trim() === '') {
                return;
              }
              
              await onMessageSubmit();
            }}
          />
          <Tooltip
            id="chat-left-buttons"
            place="top-end"
            className="shadow-lg"
            opacity={1}
            content={
              <p className='max-w-[150px] font-["Nunito_Sans"]'>
                Switch between speech and thoughts/actions, using [square brackets] for
                the latter
              </p>
            }
          />
          <ChatLeftButtons
            data-tooltip-id="chat-left-buttons"
            onClick={() => {
              inputRef?.current?.focus();
              setChatMode(chatMode === 'chat' ? 'brackets' : 'chat');
              if (chatMode === 'brackets') {
                if (newMessageText.includes(' []')) {
                  setNewMessageText(newMessageText.replace(' []', ''));
                } else {
                  setNewMessageText(newMessageText + ' ');
                }
              } else {
                setNewMessageText(newMessageText.trim() + ' []');
              }
            }}
          >
            <div className="cursor-pointer py-[18px] pl-[16px]">
              <ChatLeftIconPlaceholder className="rounded-tl-full rounded-bl-full">
                <ChatLeftIcon
                  src={chat_icon}
                  alt={'chat'}
                  selected={chatMode === 'chat'}
                />
              </ChatLeftIconPlaceholder>
            </div>
            <div className="cursor-pointer py-[18px] pr-[16px]">
              <ChatLeftIconPlaceholder className="rounded-tr-full rounded-br-full">
                <ChatLeftIcon
                  src={brackets_icon}
                  alt={'brackets'}
                  selected={chatMode === 'brackets'}
                />
              </ChatLeftIconPlaceholder>
            </div>
          </ChatLeftButtons>
          {renderSendButton()}
          {isSubscriptionModalOpen && (
            <SubscriptionModal
              isOpen
              onClose={() => setIsSubscriptionModalOpen(false)}
              analyticsData={{
                rendered_for_reason: 'custom_scenario_photo_generation'
              }}
            />
          )}
          {isBuyTokensModalOpen && (
            <BuyTokensModal
              isOpen
              onClose={() => setIsBuyTokensModalOpen(false)}
              analyticsDataRenderReason='custom_scenario_photo_generation'
            />
          )}
        </div>
      </div>
    </UserInputPlaceholder>
  );
};

export default UserInputComponent;

const UserInputPlaceholder = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  right: 0px;
  padding: 24px;
  padding-top: 12px;
  color: white;
  font-family: 'Nunito Sans';
  font-size: 16px;
  border-radius: 8px 8px 0px 0px;
  z-index: 5;
  @media (max-width: 720px) {
    padding: 12px;
  }
`;

const StyledTooltip = styled(Tooltip)`
  &.suggestion-tooltip {
    background: none !important;
    padding: 0 !important;
    border-radius: 0 !important;
    opacity: 1 !important;
    filter: none !important;
    box-shadow: none !important;
    margin-top: -16px !important;
  }
`;

const TokenAmount = styled.div`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;
  color: #FFFFFF;
`;

const ChatLeftButtons = styled.div`
width: fit-content;
border-radius: 20px;
display: flex;
align-items: center;
position: absolute;
top: 0;
left: 0;
`;

const ChatLeftIconPlaceholder = styled.div`
background - color: rgba(40, 40, 40, 1);
`;

const ChatLeftIcon = styled.img`
opacity: ${props => (props?.selected ? '1' : '0.5')};
width: 24px;
height: 24px;
background-color: ${props => (props?.selected ? 'rgba(255, 126, 85, 1)' : 'transparent')};
border-radius: 16px;
padding: 4px;
`;

const UserInput = styled.input`background: rgba(19, 19, 19, 1);
border-radius: 15px;
color: white;
height: 60px;
outline: none;
padding-left: 20px;
padding-right: 60px;
border: 1px solid transparent;

  :focus {
  border: 1px solid rgba(255, 126, 85, 1);
}

  ::placeholder {
  color: rgba(105, 105, 105, 1);
}
  
`; const SendButton = styled.div`
  cursor: ${props => (props.$isInactiveButton ? 'default' : 'pointer')};
  width: ${props => (props.$isCameraIcon ? '76px' : '40px')};
  height: ${props => (props.$isCameraIcon ? '28px' : '40px')};
  border-radius: ${props => (props.$isCameraIcon ? '6px' : '1000px')};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${props => (props.$isCameraIcon ? 'flex-start' : 'center')};
  position: absolute;
  top: ${props => (props.$isCameraIcon ? '16px' : '10px')};
  right: ${props => (props.$isCameraIcon ? '1rem' : '10px')};
  background: ${props => {
    if (props.$isInactiveButton) return 'transparent';
    if (props.$isCameraIcon) return 'linear-gradient(90deg, #9E5EEE 0%, #B759EA 32%, #C65DE9 66%, #B885F4 100%)';
    return 'transparent';
  }};
  opacity: ${props => (props.$isInactiveButton ? '0.5' : '1')};
  overflow: hidden;
`;
const CameraSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 100%;
`;

const TokenSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 38px;
  height: 100%;
  background-color: rgba(19, 19, 19, 0.5);
  color: white;
  font-size: 14px;
  font-weight: 600;
`;

const ArrowImg = styled.img`
  height: ${props => (props.src === camera_icon ? '16px' : '28px')};
  width: ${props => (props.src === camera_icon ? '18px' : 'auto')};
  pointer-events: none;
`;

const TooltipContent = styled.div`
  padding: 16px;
  background: #100D0B;
  border-radius: 16px;
  position: relative;
  display: block;

  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 24px;
    transform: translateX(-50%) rotate(45deg);
    width: 16px;
    height: 16px;
    background: #100D0B;
  }
`;

const InitialSuggestionTooltip = styled.div`
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 16px;
  line-height: 18px;
  color: #FFFFFF;
  padding: 12px 6px;
  border-radius: 12px;
  border: 1px solid #9E5EEE;
  display: block;
  max-width: 658px;
  word-wrap: break-word;

  @media (max-width: 720px) {
    font-size: 14px;
    line-height: 19px;
    white-space: wrap;
    max-width: 320px;
  }

  @media (max-width: 376px) {
    font-size: 14px;
    line-height: 19px;
    white-space: wrap;
    max-width: 280px;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;
